import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/shared/api';

// Buendelt die Kontext-Queries eines Threads: Buchung → (Property, Gast) und, wenn der Thread an
// einer OTA-Reservierung haengt, deren Apartments. Teilt die Query-Keys mit den Booking-/Property-/
// Guest-Detailseiten → gemeinsamer Cache.
// PII (Gast) wird NUR bei canViewPii gefetcht (Admin/Staff) — Owner/Housekeeping sehen die Karte nie.
export function useThreadContext({
  threadId,
  bookingId,
  propertyId,
  otaReservationId,
  canViewPii,
}: {
  threadId: string;
  bookingId: string | null;
  propertyId: string | null;
  otaReservationId: string | null;
  canViewPii: boolean;
}) {
  const api = useApi();

  const booking = useQuery({
    queryKey: ['bookings', 'detail', bookingId],
    queryFn: () => api.bookings.get(bookingId!),
    enabled: !!bookingId,
  });

  // Property-Id bevorzugt vom Thread (auch fuer Inquiries ohne Buchung vorhanden), sonst aus der Buchung.
  const resolvedPropertyId = propertyId ?? booking.data?.propertyId ?? null;
  const property = useQuery({
    queryKey: ['properties', 'detail', resolvedPropertyId],
    queryFn: () => api.properties.get(resolvedPropertyId!),
    enabled: !!resolvedPropertyId,
  });

  const guestProfileId = booking.data?.guestProfileId ?? null;
  const guest = useQuery({
    queryKey: ['guests', 'detail', guestProfileId],
    queryFn: () => api.guests.get(guestProfileId!),
    enabled: !!guestProfileId && canViewPii,
  });

  // Die Apartments der Reservierung — nur wenn der Thread an einer haengt. Der Server liefert sie
  // bereits zugriffsgefiltert; der Client zaehlt nichts hoch.
  const apartments = useQuery({
    queryKey: ['inbox', 'apartments', threadId],
    queryFn: () => api.messaging.apartments(threadId),
    enabled: !!otaReservationId,
  });

  return { booking, property, guest, guestProfileId, apartments };
}
